// Security monitoring and logging utilities

// Security event types
export const SECURITY_EVENTS = {
  ROLE_VERIFICATION: 'ROLE_VERIFICATION',
  MEMBERSHIP_VERIFICATION: 'MEMBERSHIP_VERIFICATION',
  ACCESS_ATTEMPT: 'ACCESS_ATTEMPT',
  SECURITY_VIOLATION: 'SECURITY_VIOLATION',
  CACHE_HIT: 'CACHE_HIT',
  CACHE_MISS: 'CACHE_MISS'
};

// Security event severity levels
export const SECURITY_LEVELS = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
  CRITICAL: 'CRITICAL'
};

// Security monitoring configuration
const SECURITY_CONFIG = {
  enableLogging: true,
  enableConsoleLogging: true,
  enablePerformanceMonitoring: true,
  logRetentionDays: 30,
  maxLogEntries: 1000
};

// Security event storage (in production, this would go to a proper logging service)
const securityEventLog = [];

// Log security event
export const logSecurityEvent = (eventType, level, details) => {
  const event = {
    timestamp: new Date().toISOString(),
    eventType,
    level,
    details,
    sessionId: getSessionId(),
    userAgent: navigator.userAgent,
    url: window.location.href
  };

  // Add to local log
  securityEventLog.push(event);

  // Maintain log size
  if (securityEventLog.length > SECURITY_CONFIG.maxLogEntries) {
    securityEventLog.shift();
  }

  // Console logging disabled in production

  // In production, send to security monitoring service
  if (SECURITY_CONFIG.enableLogging) {
    sendToSecurityMonitoring(event);
  }

  return event;
};

// Log role verification attempt
export const logRoleVerification = (userId, organizationId, requiredRole, success, error = null) => {
  const level = success ? SECURITY_LEVELS.INFO : SECURITY_LEVELS.WARNING;
  const details = {
    userId,
    organizationId,
    requiredRole,
    success,
    error: error?.message || null,
    timestamp: Date.now()
  };

  return logSecurityEvent(SECURITY_EVENTS.ROLE_VERIFICATION, level, details);
};

// Log membership verification attempt
export const logMembershipVerification = (userId, organizationId, success, error = null) => {
  const level = success ? SECURITY_LEVELS.INFO : SECURITY_LEVELS.WARNING;
  const details = {
    userId,
    organizationId,
    success,
    error: error?.message || null,
    timestamp: Date.now()
  };

  return logSecurityEvent(SECURITY_EVENTS.MEMBERSHIP_VERIFICATION, level, details);
};

// Log access attempt
export const logAccessAttempt = (userId, organizationId, operation, success, requiredRole = null) => {
  const level = success ? SECURITY_LEVELS.INFO : SECURITY_LEVELS.ERROR;
  const details = {
    userId,
    organizationId,
    operation,
    success,
    requiredRole,
    timestamp: Date.now()
  };

  return logSecurityEvent(SECURITY_EVENTS.ACCESS_ATTEMPT, level, details);
};

// Log security violation
export const logSecurityViolation = (userId, organizationId, violationType, details) => {
  const event = {
    userId,
    organizationId,
    violationType,
    details,
    timestamp: Date.now()
  };

  return logSecurityEvent(SECURITY_EVENTS.SECURITY_VIOLATION, SECURITY_LEVELS.CRITICAL, event);
};

// Log cache performance
export const logCachePerformance = (cacheType, hit, key, responseTime) => {
  const level = SECURITY_LEVELS.INFO;
  const details = {
    cacheType,
    hit,
    key,
    responseTime,
    timestamp: Date.now()
  };

  return logSecurityEvent(hit ? SECURITY_EVENTS.CACHE_HIT : SECURITY_EVENTS.CACHE_MISS, level, details);
};

// Get security event log
export const getSecurityEventLog = (filter = {}) => {
  let filteredLog = [...securityEventLog];

  // Apply filters
  if (filter.eventType) {
    filteredLog = filteredLog.filter(event => event.eventType === filter.eventType);
  }
  if (filter.level) {
    filteredLog = filteredLog.filter(event => event.level === filter.level);
  }
  if (filter.userId) {
    filteredLog = filteredLog.filter(event => event.details?.userId === filter.userId);
  }
  if (filter.organizationId) {
    filteredLog = filteredLog.filter(event => event.details?.organizationId === filter.organizationId);
  }
  if (filter.startDate) {
    filteredLog = filteredLog.filter(event => new Date(event.timestamp) >= new Date(filter.startDate));
  }
  if (filter.endDate) {
    filteredLog = filteredLog.filter(event => new Date(event.timestamp) <= new Date(filter.endDate));
  }

  return filteredLog;
};

// Get security statistics
export const getSecurityStats = () => {
  const now = Date.now();
  const oneHourAgo = now - (60 * 60 * 1000);
  const oneDayAgo = now - (24 * 60 * 60 * 1000);

  const recentEvents = securityEventLog.filter(event => 
    new Date(event.timestamp).getTime() > oneHourAgo
  );

  const dailyEvents = securityEventLog.filter(event => 
    new Date(event.timestamp).getTime() > oneDayAgo
  );

  return {
    totalEvents: securityEventLog.length,
    recentEvents: recentEvents.length,
    dailyEvents: dailyEvents.length,
    eventsByType: countEventsByType(securityEventLog),
    eventsByLevel: countEventsByLevel(securityEventLog),
    recentViolations: recentEvents.filter(event => event.level === SECURITY_LEVELS.CRITICAL).length,
    cacheHitRate: calculateCacheHitRate()
  };
};

// Helper functions
const getSessionId = () => {
  // Generate or retrieve session ID
  if (!window.sessionStorage.getItem('securitySessionId')) {
    const sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    window.sessionStorage.setItem('securitySessionId', sessionId);
  }
  return window.sessionStorage.getItem('securitySessionId');
};

const countEventsByType = (events) => {
  return events.reduce((acc, event) => {
    acc[event.eventType] = (acc[event.eventType] || 0) + 1;
    return acc;
  }, {});
};

const countEventsByLevel = (events) => {
  return events.reduce((acc, event) => {
    acc[event.level] = (acc[event.level] || 0) + 1;
    return acc;
  }, {});
};

const calculateCacheHitRate = () => {
  const cacheEvents = securityEventLog.filter(event => 
    event.eventType === SECURITY_EVENTS.CACHE_HIT || event.eventType === SECURITY_EVENTS.CACHE_MISS
  );
  
  if (cacheEvents.length === 0) return 0;
  
  const hits = cacheEvents.filter(event => event.eventType === SECURITY_EVENTS.CACHE_HIT).length;
  return (hits / cacheEvents.length) * 100;
};

// Send to security monitoring service (placeholder for production)
const sendToSecurityMonitoring = (event) => {
  // In production, this would send to a proper security monitoring service
  // For now, we'll just store it locally
  try {
    // Could send to Firebase Analytics, external service, etc.
    if (window.gtag) {
      window.gtag('event', 'security_event', {
        event_type: event.eventType,
        security_level: event.level,
        user_id: event.details?.userId || 'unknown',
        organization_id: event.details?.organizationId || 'unknown'
      });
    }
  } catch (error) {
    // Silently handle monitoring service errors
  }
};

// Performance monitoring
export const measureSecurityOperation = async (operationName, operation) => {
  const startTime = performance.now();
  
  try {
    const result = await operation();
    const endTime = performance.now();
    const responseTime = endTime - startTime;
    
    // Log performance metrics
    logSecurityEvent('PERFORMANCE_MONITORING', SECURITY_LEVELS.INFO, {
      operation: operationName,
      responseTime,
      success: true,
      timestamp: Date.now()
    });
    
    return result;
  } catch (error) {
    const endTime = performance.now();
    const responseTime = endTime - startTime;
    
    // Log performance metrics for failed operations
    logSecurityEvent('PERFORMANCE_MONITORING', SECURITY_LEVELS.WARNING, {
      operation: operationName,
      responseTime,
      success: false,
      error: error.message,
      timestamp: Date.now()
    });
    
    throw error;
  }
};

// Export configuration for external use
export const getSecurityConfig = () => ({ ...SECURITY_CONFIG });
export const updateSecurityConfig = (updates) => {
  Object.assign(SECURITY_CONFIG, updates);
}; 